import { useFormikContext } from "formik";
import isEqual from "lodash/isEqual";
import some from "lodash/some";
import sortBy from "lodash/sortBy";
import zip from "lodash/zip";
import React, { useMemo } from "react";

import { useSelector } from "react-redux";

import { getCurrentIndividual } from "../../../../selectors/individualSelectors";
import { getTempPrerequisites } from "../../../../selectors/settingSelectors";
import ActionModalFooter from "../../../General/ActionModal/ActionModalFooter";
import Button from "../../../forms/custom/Button";

import {
  allChecklistItemsComplete,
  detectChecklistItemChange,
} from "./fieldHelpers";

const submitValuesChanged = (currValues, initialValues) => {
  if (
    currValues.checklistItems.length !== initialValues.checklistItems.length
  ) {
    return true;
  }

  return some(
    zip(currValues.checklistItems, initialValues.checklistItems),
    ([currItem, initialItem]) => {
      if (!currItem || !initialItem) return false;
      return detectChecklistItemChange(initialItem, currItem);
    }
  );
};

const prerequisitesChanged = (currValues, initialValues) => {
  const currIds = sortBy(currValues.checklistItems.map((item) => item.id));
  const initialIds = sortBy(
    initialValues.checklistItems.map((item) => item.id)
  );
  return !isEqual(currIds, initialIds);
};

function FormFooter({
  initialValues,
  isEditMode,
  setIsEditMode,
  candidate,
  handleEditModeSubmit,
}) {
  const currentIndividual = useSelector(getCurrentIndividual);
  const allTempPrerequisites = useSelector(getTempPrerequisites);
  const contract = useSelector(
    (state) => state.contracts.items[candidate.attributes.contractId]
  );
  const { values, isSubmitting, handleSubmit, resetForm } = useFormikContext();

  const isDisabled = useMemo(
    () => !submitValuesChanged(values, initialValues),
    [initialValues, values]
  );

  const isCompleteOnSubmit = useMemo(
    () => allChecklistItemsComplete(values.checklistItems, currentIndividual),
    [values, currentIndividual]
  );

  const prereqsChanged = useMemo(
    () => prerequisitesChanged(values, initialValues),
    [values, initialValues]
  );

  const hasCustomPrerequisites = useMemo(() => {
    if (!currentIndividual.recruiter) {
      return true;
    }

    if (!allTempPrerequisites) {
      return false;
    }

    return Object.values(allTempPrerequisites).some((prereq) => {
      const attr = prereq.attributes || prereq;
      const initiatorParty = attr.initiator_party || attr.initiatorParty;

      if (initiatorParty === "employer") {
        return false;
      }

      return !attr.default;
    });
  }, [currentIndividual, allTempPrerequisites]);

  const handleEditClick = () => {
    setIsEditMode(true);
  };

  const handleCancelEdit = () => {
    resetForm();
    setIsEditMode(false);
  };

  const handleSave = async () => {
    await handleEditModeSubmit();
    setIsEditMode(false);
  };

  if (isEditMode) {
    return (
      <ActionModalFooter
        isSubmitting={isSubmitting}
        customButtons={
          <div style={{ display: "flex", gap: "8px" }}>
            <Button
              color="grey"
              variant="secondary"
              onClick={handleCancelEdit}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              color="blue"
              variant="primary"
              onClick={handleSave}
              disabled={!prereqsChanged || isSubmitting}
            >
              Save
            </Button>
          </div>
        }
      />
    );
  }

  return (
    <ActionModalFooter
      isSubmitting={isSubmitting}
      customButtons={
        <div style={{ display: "flex", gap: "8px" }}>
          {hasCustomPrerequisites && (
            <Button
              color="blue"
              variant="secondary"
              onClick={handleEditClick}
              disabled={isSubmitting || !!contract?.attributes?.activatedAt}
            >
              Edit Steps
            </Button>
          )}
          <Button
            color="blue"
            variant="primary"
            onClick={handleSubmit}
            disabled={isDisabled || isSubmitting}
          >
            {isCompleteOnSubmit ? "Complete" : "Update"}
          </Button>
        </div>
      }
    />
  );
}

export default FormFooter;
